// src/Pages/AcceptInvitation.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { EXAMS_API } from '../config/api';
import './AcceptInvitation.css';

export default function AcceptInvitation() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();

  const inviteToken = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);

  /* ============================================================
     1. Validate token on mount
     ============================================================ */
  useEffect(() => {
    if (!inviteToken) {
      setError('Missing invitation token. Please use the link from your email.');
      setLoading(false);
      return;
    }

    (async () => {
      setLoading(true);
      setError('');
      const url = `${EXAMS_API}/invitations/validate/${inviteToken}`;
      console.log('[AcceptInvite] Validating:', url);

      try {
        const res = await fetch(url);
        console.log('[AcceptInvite] Status:', res.status);

        const raw = await res.text();
        let data;
        try {
          data = JSON.parse(raw);
        } catch {
          throw new Error(
            `Server returned non-JSON (HTTP ${res.status}). Check backend at ${EXAMS_API}.`
          );
        }

        if (!res.ok || !data.success) {
          throw new Error(data.message || 'Invalid or expired invitation link.');
        }

        setInvite(data.data);
      } catch (err) {
        console.error('[AcceptInvite] Error:', err);
        if (err.message.includes('Failed to fetch')) {
          setError(
            `Could not reach the server at ${EXAMS_API}. Make sure the backend is running on http:// (not https://).`
          );
        } else {
          setError(err.message || 'Could not validate the invitation.');
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [inviteToken]);

  /* ============================================================
     2. Accept invitation and redirect to exam
     ============================================================ */
  const acceptAndGo = useCallback(async () => {
    setAccepting(true);
    const url = `${EXAMS_API}/invitations/accept/${inviteToken}`;
    console.log('[AcceptInvite] Accepting:', url);

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token || localStorage.getItem('token')}`,
        },
      });
      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Could not accept the invitation.');
      }

      console.log('[AcceptInvite] Success, going to exam:', data.data.examId);
      navigate(`/exam/${data.data.examId}`, { replace: true });
    } catch (err) {
      console.error('[AcceptInvite] Accept error:', err);
      setError(err.message);
      setAccepting(false);
    }
  }, [inviteToken, token, navigate]);

  /* Auto-accept if logged in with matching email */
  useEffect(() => {
    if (loading || error || !invite || !token) return;
    if (
      user?.email &&
      user.email.toLowerCase() === invite.email.toLowerCase()
    ) {
      acceptAndGo();
    }
  }, [loading, error, invite, token, user, acceptAndGo]);

  /* ============================================================
     3. Login redirect
     ============================================================ */
  const handleLogin = () => {
    const redirect = encodeURIComponent(
      `/exam/accept-invite?token=${inviteToken}`
    );
    navigate(
      `/login?redirect=${redirect}&email=${encodeURIComponent(
        invite?.email || ''
      )}`
    );
  };

  /* ============================================================
     RENDER
     ============================================================ */
  if (loading) {
    return (
      <div className="accept-invite-page">
        <div className="accept-invite-card">
          <div className="accept-invite-spinner" />
          <h2>Validating invitation…</h2>
          <p>Please wait while we verify your link.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="accept-invite-page">
        <div className="accept-invite-card accept-invite-error">
          <div className="accept-invite-icon">⚠️</div>
          <h2>Invitation Problem</h2>
          <p className="accept-invite-message">{error}</p>
          <p style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
            API: {EXAMS_API}
          </p>
          <div className="accept-invite-actions">
            <button
              className="accept-invite-btn primary"
              onClick={() => window.location.reload()}
            >
              Try Again
            </button>
            <button
              className="accept-invite-btn primary"
              onClick={() => navigate('/profile')}
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="accept-invite-page">
        <div className="accept-invite-card">
          <div className="accept-invite-icon">🤔</div>
          <h2>Nothing to show</h2>
          <p>The invitation link didn't return any details.</p>
        </div>
      </div>
    );
  }

  const notLoggedIn = !token;
  const wrongEmail =
    token &&
    user?.email &&
    user.email.toLowerCase() !== invite.email.toLowerCase();

  return (
    <div className="accept-invite-page">
      <div className="accept-invite-card">
        <div className="accept-invite-icon">📩</div>
        <span className="accept-invite-eyebrow">EXAM INVITATION</span>
        <h2>{invite.examTitle}</h2>
        <p className="accept-invite-sub">
          You've been invited to take this exam. Duration:{' '}
          <strong>{invite.durationInMinutes} minutes</strong>.
        </p>

        <div className="accept-invite-info">
          <div className="accept-invite-info-row">
            <span>Invited email</span>
            <strong>{invite.email}</strong>
          </div>
          {user?.email && (
            <div className="accept-invite-info-row">
              <span>Logged in as</span>
              <strong className={wrongEmail ? 'mismatch' : 'match'}>
                {user.email} {wrongEmail ? '(mismatch)' : ''}
              </strong>
            </div>
          )}
        </div>

        {wrongEmail && (
          <div className="accept-invite-warning">
            ⚠️ You're logged in with a <strong>different email</strong>. Please
            log in with <strong>{invite.email}</strong> to accept this
            invitation.
          </div>
        )}

        <div className="accept-invite-actions">
          {notLoggedIn && (
            <button className="accept-invite-btn primary" onClick={handleLogin}>
              Log In / Register to Continue
            </button>
          )}

          {!notLoggedIn && !wrongEmail && (
            <button
              className="accept-invite-btn primary"
              onClick={acceptAndGo}
              disabled={accepting}
            >
              {accepting ? 'Preparing exam…' : 'Start Exam →'}
            </button>
          )}

          {!notLoggedIn && wrongEmail && (
            <button
              className="accept-invite-btn primary"
              onClick={() => {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                window.location.href = `/login?redirect=${encodeURIComponent(
                  `/exam/accept-invite?token=${inviteToken}`
                )}`;
              }}
            >
              Log Out & Switch Account
            </button>
          )}
        </div>
      </div>
    </div>
  );
}